import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  InboxIcon, 
  PaperAirplaneIcon, 
  DocumentIcon, 
  TrashIcon, 
  ArchiveBoxIcon,
  StarIcon,
  PencilSquareIcon,
  Bars3Icon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { mailApi } from '../api/mailApi';
import toast from 'react-hot-toast';

const Mailbox = () => {
  const navigate = useNavigate();
  const [currentFolder, setCurrentFolder] = useState('inbox');
  const [mails, setMails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMail, setSelectedMail] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const folders = [
    { id: 'inbox', name: 'Inbox', icon: InboxIcon },
    { id: 'starred', name: 'Starred', icon: StarIcon },
    { id: 'sent', name: 'Sent', icon: PaperAirplaneIcon },
    { id: 'drafts', name: 'Drafts', icon: DocumentIcon },
    { id: 'archive', name: 'Archive', icon: ArchiveBoxIcon },
    { id: 'trash', name: 'Trash', icon: TrashIcon },
  ];

  useEffect(() => {
    fetchMails();
  }, [currentFolder]);

  const fetchMails = async () => {
    setLoading(true);
    setSelectedMail(null);
    try {
      const res = await mailApi.getMailsByFolder(currentFolder);
      if (res.data.success) {
        setMails(res.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch mails', error);
      toast.error('Failed to load mails');
    } finally {
      setLoading(false);
    }
  };

  const handleMailClick = (mail) => {
    if (mail.status === 'draft') {
      navigate(`/mails/compose?draft=${mail._id}`);
    } else {
      setSelectedMail(mail);
    }
  };

  const handleToggleStar = async (e, mail) => {
    e.stopPropagation();
    try {
      await mailApi.toggleStar(mail._id);
      setMails(mails.map(m => m._id === mail._id ? { ...m, isStarred: !m.isStarred } : m));
    } catch (error) {
      toast.error('Failed to update star');
    }
  };

  const handleMoveToTrash = async (e, mailId) => {
    e.stopPropagation();
    try {
      if (currentFolder === 'trash') {
        if(window.confirm('Are you sure you want to delete this permanently?')) {
          await mailApi.deleteMail(mailId);
          toast.success('Mail deleted permanently');
          setMails(mails.filter(m => m._id !== mailId));
          if(selectedMail?._id === mailId) setSelectedMail(null);
        }
      } else {
        await mailApi.updateStatus(mailId, 'trash');
        toast.success('Moved to Trash');
        setMails(mails.filter(m => m._id !== mailId));
        if(selectedMail?._id === mailId) setSelectedMail(null);
      }
    } catch (error) {
      toast.error('Failed to delete mail');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    if (date.toDateString() === today.toDateString()) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  return (
    <div className="h-[calc(100vh-100px)] bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 flex overflow-hidden">
      
      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-20 md:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed md:static inset-y-0 left-0 z-30 w-64 bg-gray-50 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700
        transform transition-transform duration-200 ease-in-out flex flex-col
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
        <div className="p-4 flex items-center justify-between md:block">
          <Link 
            to="/mails/compose"
            className="w-full bg-[#dc343b] hover:bg-[#b02a2f] text-white py-3 px-4 rounded-xl font-bold flex items-center justify-center gap-2 transition-colors shadow-sm"
          >
            <PencilSquareIcon className="h-5 w-5" />
            Compose
          </Link>
          <button className="md:hidden p-2" onClick={() => setIsSidebarOpen(false)}>
            <XMarkIcon className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-2">
          {folders.map(folder => (
            <button
              key={folder.id}
              onClick={() => {
                setCurrentFolder(folder.id);
                setIsSidebarOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-6 py-3 text-[14px] font-medium transition-colors ${
                currentFolder === folder.id 
                  ? 'bg-red-50 dark:bg-red-900/20 text-[#dc343b] dark:text-red-400 border-r-4 border-[#dc343b]' 
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'
              }`}
            >
              <folder.icon className="h-5 w-5" />
              {folder.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        
        {/* Mobile Header */}
        <div className="md:hidden flex items-center p-4 border-b border-gray-200 dark:border-gray-700">
          <button onClick={() => setIsSidebarOpen(true)} className="mr-3">
            <Bars3Icon className="h-6 w-6 text-gray-600 dark:text-gray-300" />
          </button>
          <h2 className="text-lg font-bold capitalize text-gray-900 dark:text-white">{currentFolder}</h2>
        </div>

        {/* Split View */}
        <div className="flex-1 flex overflow-hidden">
          
          {/* Mail List */}
          <div className={`
            w-full md:w-[350px] lg:w-[400px] flex flex-col border-r border-gray-200 dark:border-gray-700
            ${selectedMail ? 'hidden md:flex' : 'flex'}
          `}>
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 hidden md:block">
              <h2 className="text-xl font-bold capitalize text-gray-900 dark:text-white">{currentFolder}</h2>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center text-gray-500">Loading...</div>
              ) : mails.length === 0 ? (
                <div className="p-8 text-center text-gray-500">No mails in {currentFolder}</div>
              ) : (
                <div className="divide-y divide-gray-100 dark:divide-gray-800">
                  {mails.map(mail => (
                    <div 
                      key={mail._id}
                      onClick={() => handleMailClick(mail)}
                      className={`p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors ${selectedMail?._id === mail._id ? 'bg-blue-50 dark:bg-blue-900/10' : ''}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-bold text-gray-900 dark:text-white text-[14px] truncate flex-1 pr-2">
                          {mail.to[0] || 'Unknown Recipient'}
                          {mail.to.length > 1 && ` +${mail.to.length - 1}`}
                        </h4>
                        <span className="text-[12px] text-gray-500 whitespace-nowrap">
                          {formatDate(mail.createdAt)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-[13px] text-gray-600 dark:text-gray-300 truncate font-medium">
                          {mail.subject || '(No Subject)'}
                        </p>
                        <button onClick={(e) => handleToggleStar(e, mail)} className="ml-2 text-gray-400 hover:text-yellow-500">
                          {mail.isStarred ? <StarIconSolid className="h-4 w-4 text-yellow-500" /> : <StarIcon className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Mail Detail View */}
          <div className={`
            flex-1 flex flex-col bg-white dark:bg-gray-900 overflow-y-auto
            ${!selectedMail ? 'hidden md:flex items-center justify-center' : 'flex'}
          `}>
            {!selectedMail ? (
              <div className="text-center text-gray-400 dark:text-gray-600">
                <InboxIcon className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p>Select an item to read</p>
              </div>
            ) : (
              <div className="p-6 md:p-8 w-full max-w-4xl mx-auto">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <button 
                      className="md:hidden mb-4 flex items-center text-sm text-gray-500 hover:text-gray-900"
                      onClick={() => setSelectedMail(null)}
                    >
                      &larr; Back
                    </button>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                      {selectedMail.subject || '(No Subject)'}
                    </h1>
                    <div className="flex items-center gap-2 text-[14px]">
                      <span className="font-bold text-gray-700 dark:text-gray-300">To:</span>
                      <span className="text-gray-600 dark:text-gray-400">{selectedMail.to.join(', ')}</span>
                    </div>
                    {selectedMail.cc?.length > 0 && (
                      <div className="flex items-center gap-2 text-[13px] mt-1">
                        <span className="font-medium text-gray-500">CC:</span>
                        <span className="text-gray-500">{selectedMail.cc.join(', ')}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <button onClick={(e) => handleToggleStar(e, selectedMail)} className="p-2 text-gray-400 hover:text-yellow-500 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                       {selectedMail.isStarred ? <StarIconSolid className="h-5 w-5 text-yellow-500" /> : <StarIcon className="h-5 w-5" />}
                    </button>
                    <button onClick={(e) => handleMoveToTrash(e, selectedMail._id)} className="p-2 text-gray-400 hover:text-red-500 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                       <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                <div className="flex items-center gap-3 mb-8 pb-4 border-b border-gray-200 dark:border-gray-800">
                  <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center text-blue-600 dark:text-blue-400 font-bold">
                    ME
                  </div>
                  <div>
                    <p className="text-[14px] font-bold text-gray-900 dark:text-white">Admin (Khatabook)</p>
                    <p className="text-[12px] text-gray-500">{formatDate(selectedMail.createdAt)}</p>
                  </div>
                </div>

                {/* Mail Body */}
                <div 
                  className="prose dark:prose-invert max-w-none text-[15px] leading-relaxed text-gray-800 dark:text-gray-200"
                  dangerouslySetInnerHTML={{ __html: selectedMail.bodyHTML || '<p>No content</p>' }}
                />

                {/* Attachments */}
                {selectedMail.attachments?.length > 0 && (
                  <div className="mt-12 pt-6 border-t border-gray-200 dark:border-gray-800">
                    <h4 className="text-[13px] font-bold text-gray-500 uppercase tracking-wider mb-4">
                      Attachments ({selectedMail.attachments.length})
                    </h4>
                    <div className="flex flex-wrap gap-3">
                      {selectedMail.attachments.map((file, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer transition-colors">
                           <DocumentIcon className="h-6 w-6 text-gray-400" />
                           <div>
                             <p className="text-[13px] font-bold text-gray-700 dark:text-gray-300 truncate max-w-[150px]">{file.filename}</p>
                             <p className="text-[11px] text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                           </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default Mailbox;
